import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import { tokenFor } from "../../utils/tokenFor";

const FALLBACK_IMG =
  "https://rmrstorage.blob.core.windows.net/measite/MeaLogoBlackTrans.png";

export default function OptionsDialog({
  open,
  onClose,
  menuItem,
  defaultQty = 1,
  onConfirm,
}) {
  const [selectedOptions, setSelectedOptions] = useState({});
  const [qty, setQty] = useState(defaultQty);

  useEffect(() => {
    if (open) {
      setSelectedOptions({});
      setQty(defaultQty || 1);
    }
  }, [open, defaultQty, menuItem?.menuItemId]);

  const item = menuItem || {};
  const basePrice = Number(item.basePrice || 0);

  const images = useMemo(() => {
    const raw = Array.isArray(item?.images) ? item.images : [];

    const cleaned = raw
      .map((img, idx) => ({
        imageUrl: img?.imageUrl || "",
        thumbnailUrl: img?.thumbnailUrl || "",
        altText: img?.altText || item.name || `Image ${idx + 1}`,
        displayOrder: img?.displayOrder ?? idx,
        isPrimary: !!img?.isPrimary,
        active: img?.active !== false,
      }))
      .filter((img) => img.imageUrl)
      .sort((a, b) => {
        const primaryDiff = Number(!!b.isPrimary) - Number(!!a.isPrimary);
        if (primaryDiff !== 0) return primaryDiff;
        return Number(a.displayOrder ?? 0) - Number(b.displayOrder ?? 0);
      });

    if (cleaned.length > 0) return cleaned;

    return [
      {
        imageUrl: item.imageUrl || FALLBACK_IMG,
        thumbnailUrl: "",
        altText: item.name || "Menu item",
        displayOrder: 0,
        isPrimary: true,
        active: true,
      },
    ];
  }, [item]);

  const normalizedOptions = useMemo(() => {
    const opts = Array.isArray(menuItem?.options) ? menuItem.options : [];

    return [...opts]
      .map((opt) => ({
        menuItemOptionId: opt?.menuItemOptionId,
        name: opt?.name || "",
        type: opt?.type || "single",
        sortOrder: Number(opt?.sortOrder ?? 9999),
        isActive: opt?.isActive !== false,
        items: Array.isArray(opt?.items)
          ? [...opt.items]
              .filter((x) => x?.isActive !== false)
              .sort((a, b) => {
                const sortDiff = Number(a?.sortOrder ?? 9999) - Number(b?.sortOrder ?? 9999);
                if (sortDiff !== 0) return sortDiff;
                return String(a?.name || "").localeCompare(String(b?.name || ""));
              })
          : [],
      }))
      .filter((opt) => opt.menuItemOptionId != null && opt.isActive)
      .sort((a, b) => {
        const sortDiff = Number(a.sortOrder ?? 9999) - Number(b.sortOrder ?? 9999);
        if (sortDiff !== 0) return sortDiff;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [menuItem]);

  const getOptionType = (option) => {
    const raw = String(option?.type ?? "single").toLowerCase();

    if (raw === "2" || raw === "multi" || raw === "multiple" || raw === "checkbox")
      return "multi";

    return "single";
  };

  const isRequiredOption = (option) => {
    const name = String(option?.name || "").toLowerCase().trim();
    const isMulti = getOptionType(option) === "multi";

    if (isMulti) return false;
    if (name === "add on") return false;
    if (name.includes("add on")) return false;

    return true;
  };

  const handleOptionChange = (option, optItem, isMulti, checked) => {
    const optionId = option.menuItemOptionId;
    const token = tokenFor(option, optItem);

    setSelectedOptions((prev) => {
      if (!isMulti) {
        return { ...prev, [optionId]: token };
      }

      const curr = Array.isArray(prev[optionId]) ? prev[optionId] : [];
      const next = checked
        ? [...new Set([...curr, token])]
        : curr.filter((t) => t !== token);

      const copy = { ...prev };
      if (next.length) copy[optionId] = next;
      else delete copy[optionId];
      return copy;
    });
  };

  const finalPrice = useMemo(() => {
    let total = Number(item.basePrice || 0);

    normalizedOptions.forEach((option) => {
      const optionId = option.menuItemOptionId;
      const sel = selectedOptions[optionId];

      const addOne = (tokenStr) => {
        try {
          const parsed = JSON.parse(tokenStr);
          total += Number(parsed.price || 0);
        } catch {
          // ignore malformed token
        }
      };

      if (Array.isArray(sel)) sel.forEach(addOne);
      else if (typeof sel === "string" && sel) addOne(sel);
    });

    return total * qty;
  }, [item.basePrice, normalizedOptions, selectedOptions, qty]);

  const confirm = () => {
    const missing = normalizedOptions.filter((option) => {
      if (!isRequiredOption(option)) return false;

      const hasSelection =
        typeof selectedOptions[option.menuItemOptionId] === "string" &&
        !!selectedOptions[option.menuItemOptionId];

      return !hasSelection;
    });

    if (missing.length) {
      alert("Please select an option for: " + missing[0].name);
      return;
    }

    onConfirm({
      selections: selectedOptions,
      finalPrice,
      quantity: qty,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Choose Options</DialogTitle>

      <DialogContent dividers>
        {menuItem && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box
              component="img"
              src={images[0]?.imageUrl || FALLBACK_IMG}
              alt={item.name}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = FALLBACK_IMG;
              }}
              sx={{
                width: { xs: "100%", sm: 180 },
                height: { xs: 180, sm: 150 },
                objectFit: "cover",
                borderRadius: 1.5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 0.5 }}
                noWrap
                title={item.name}
              >
                {item.name}
              </Typography>

              {item.description && (
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    lineHeight: 1.5,
                    whiteSpace: "pre-line",
                  }}
                >
                  {item.description}
                </Typography>
              )}

              {basePrice > 0 && (
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, mt: 0.5 }}
                >
                  Base price: ${basePrice.toFixed(2)}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {normalizedOptions.map((option) => {
          const isMulti = getOptionType(option) === "multi";
          const optionId = option.menuItemOptionId;
          const currentValue = selectedOptions[optionId];
          const required = isRequiredOption(option);

          return (
            <Box key={optionId} mb={2.5}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    fontWeight: required ? 900 : 700,
                    mb: 0.5,
                    color: required ? "text.primary" : "text.secondary",
                  }}
                >
                  {option.name}
                  {required && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        fontWeight: 900,
                        color: "error.main",
                        fontSize: "0.82rem",
                      }}
                    >
                      (Required)
                    </Typography>
                  )}
                </FormLabel>

                {!isMulti ? (
                  <RadioGroup
                    value={typeof currentValue === "string" ? currentValue : ""}
                    onChange={(e) => {
                      const chosenToken = e.target.value;
                      const chosen = option.items.find(
                        (optItem) => tokenFor(option, optItem) === chosenToken
                      );

                      if (chosen) {
                        handleOptionChange(option, chosen, false, true);
                      }
                    }}
                  >
                    {option.items.map((optItem) => (
                      <FormControlLabel
                        key={optItem.menuItemOptionItemId}
                        value={tokenFor(option, optItem)}
                        control={<Radio size="small" />}
                        label={
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              gap: 2,
                            }}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {optItem.name}
                              </Typography>
                              {!!optItem.description && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {optItem.description}
                                </Typography>
                              )}
                            </Box>

                            {Number(optItem.price || 0) > 0 && (
                              <Typography variant="body2" color="text.secondary">
                                +${Number(optItem.price).toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ m: 0.25 }}
                      />
                    ))}
                  </RadioGroup>
                ) : (
                  <FormGroup sx={{ mt: 0.5 }}>
                    {option.items.map((optItem) => {
                      const t = tokenFor(option, optItem);
                      const checked =
                        Array.isArray(currentValue) && currentValue.includes(t);

                      return (
                        <FormControlLabel
                          key={optItem.menuItemOptionItemId}
                          control={
                            <Checkbox
                              size="small"
                              checked={!!checked}
                              onChange={(e) =>
                                handleOptionChange(
                                  option,
                                  optItem,
                                  true,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                                gap: 2,
                              }}
                            >
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {optItem.name}
                                </Typography>
                                {!!optItem.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {optItem.description}
                                  </Typography>
                                )}
                              </Box>

                              {Number(optItem.price || 0) > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  +${Number(optItem.price).toFixed(2)}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ m: 0.25 }}
                        />
                      );
                    })}
                  </FormGroup>
                )}
              </FormControl>
            </Box>
          );
        })}

        <Divider sx={{ my: 2 }} />

        <Box display="flex" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Quantity</InputLabel>
            <Select
              value={qty}
              label="Quantity"
              onChange={(e) => setQty(Number(e.target.value))}
            >
              {[...Array(10)].map((_, i) => (
                <MenuItem key={i + 1} value={i + 1}>
                  {i + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ ml: "auto", textAlign: "right" }}>
            <Typography variant="caption" color="text.secondary">
              Subtotal
            </Typography>
            <Typography variant="h6" sx={{ m: 0, fontWeight: 800 }}>
              ${finalPrice.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={confirm}
          sx={{ borderRadius: 999, px: 2.5 }}
        >
          Add to Cart
        </Button>
      </DialogActions>
    </Dialog>
  );
}